import {
	CheckCircle2,
	CircleDashed,
	Clock,
	type LucideIcon,
	ShieldAlert,
} from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../lib/cn";

export type TrustStatus = "verified" | "recorded" | "processing" | "tamper";

/**
 * Single controlled-lexicon source of truth for a record's trust standing.
 * Plain language only — no internal jargon (hash, merkle, anchor, batch).
 * `label` is the short text the badge renders; `verdict` is the one-sentence
 * plain-language explanation for callers to show in tooltips / detail panels.
 */
export const TRUST_STATUS_COPY: Record<
	TrustStatus,
	{ label: string; verdict: string }
> = {
	verified: {
		label: "Verified",
		verdict:
			"This record is confirmed authentic and permanently published on a public, independent ledger.",
	},
	recorded: {
		label: "Recorded",
		verdict:
			"This record is sealed and is being confirmed on the public ledger.",
	},
	processing: {
		label: "Processing",
		verdict: "This record was received and is being prepared for sealing.",
	},
	tamper: {
		label: "Integrity issue",
		verdict:
			"This record could not be confirmed — it needs review, so contact the issuer.",
	},
};

interface TrustStatusStyle {
	icon: LucideIcon;
	colorClass: string;
	borderClass: string;
}

const TRUST_STATUS_STYLES: Record<TrustStatus, TrustStatusStyle> = {
	verified: {
		icon: CheckCircle2,
		colorClass: "text-statusSuccess",
		borderClass: "border-statusSuccess",
	},
	recorded: {
		icon: Clock,
		colorClass: "text-statusInfo",
		borderClass: "border-statusInfo",
	},
	processing: {
		icon: CircleDashed,
		colorClass: "text-mutedForeground",
		borderClass: "border-border",
	},
	tamper: {
		icon: ShieldAlert,
		colorClass: "text-destructive",
		borderClass: "border-destructive",
	},
};

export interface TrustBadgeProps {
	status: TrustStatus;
	showLabel?: boolean;
	label?: string;
	className?: string;
}

function TrustBadge({
	status,
	showLabel = true,
	label,
	className,
}: TrustBadgeProps): ReactNode {
	const style = TRUST_STATUS_STYLES[status];
	const Icon = style.icon;
	const resolvedLabel = label ?? TRUST_STATUS_COPY[status].label;

	// <output> carries an implicit ARIA role of "status", exposing the meaning
	// to screen readers via aria-label without leaking the raw tone word.
	return (
		<output
			aria-label={resolvedLabel}
			className={cn(
				"inline-flex items-center gap-1.5 rounded-full border bg-muted px-2.5 py-0.5 text-xs font-medium",
				style.colorClass,
				style.borderClass,
				className,
			)}
		>
			<Icon
				className={cn("h-3.5 w-3.5 shrink-0", style.colorClass)}
				aria-hidden="true"
			/>
			{showLabel && <span>{resolvedLabel}</span>}
		</output>
	);
}
TrustBadge.displayName = "TrustBadge";

/**
 * Pure presentational mapping from raw record flags to a single trust status.
 * Tamper takes precedence over everything else.
 */
export function deriveTrustStatus(input: {
	verified: boolean;
	anchored: boolean;
	tampered?: boolean;
}): TrustStatus {
	if (input.tampered) {
		return "tamper";
	}
	if (input.verified && input.anchored) {
		return "verified";
	}
	if (input.verified && !input.anchored) {
		return "recorded";
	}
	return "processing";
}

export default TrustBadge;
export { TrustBadge };
